'use client';
import { useForm, SubmitHandler } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import Layout from '@/src/components/layout/Layout';
import Wrapper from '@/src/components/ui/wrapper/Wrapper';
import Breadcrumbs from '@/src/components/ui/breadcrumbs/Breadcrumbs';
import styles from './Contacts.module.scss';
import Button from '@/src/components/ui/button/Button';
import classNames from 'classnames';
import { contactFormSchema } from './yupSchema';
import { getFormData } from '@/src/lib/helpers/getFormData';
import { FC, useEffect, useState } from 'react';
import { LoaderIcon } from '@/src/components/ui/icons/loaderIcon/LoaderIcon';
import { SuccessIcon } from '@/src/components/ui/icons/successIcon/SuccessIcon';
import { ErrorIcon } from '@/src/components/ui/icons/errorIcon/ErrorIcon';
import createAnimationArr from '@/src/lib/helpers/createAnimationArr';
import { useInView } from 'react-intersection-observer';
import FormAgreements from '@/src/components/ui/formAgreements/FormAgreements';
import { defaultEndpoint } from '@/src/lib/constants/constants';
import SubscribeSection from '@/src/components/sections/subscribeSection/SubscribeSection';

interface Inputs {
  Name: string;
  Email: string;
  Message: string;
  AGREEMENT: boolean;
  SUBSCRIBE: boolean;
  TYPE_FORM: string;
  PORTFOLIO: string;
  Theme: string;
}

interface ContactFormState {
  isLoading: boolean;
  submitted: boolean;
  isEmailSent: boolean;
}

const Contacts: FC = () => {
  const [contactFormState, setContactFormState] = useState<ContactFormState>({
    isLoading: false,
    submitted: false,
    isEmailSent: false,
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<Inputs>({
    mode: 'all',
    resolver: yupResolver(contactFormSchema),
  });

  const postData = async (data: Inputs) => {
    setContactFormState((prev) => {
      return {
        ...prev,
        isLoading: true,
      };
    });

    const formData = getFormData(data);

    const endpoint = process.env.NEXT_PUBLIC_FORM_ENDPOINT || defaultEndpoint;

    try {
      const response = await fetch(endpoint, {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        throw new Error(`Error: ${response.status} ${response.statusText}`);
      }

      setContactFormState(() => {
        return {
          submitted: true,
          isEmailSent: true,
          isLoading: false,
        };
      });

      reset();
    } catch (error) {
      console.error('Error submitting the form:', error);
      setContactFormState(() => {
        return {
          submitted: true,
          isEmailSent: false,
          isLoading: false,
        };
      });
    }
  };

  const onSubmit: SubmitHandler<Inputs> = (data) => {
    postData(data);
  };

  const isValidationErrors = !!Object.keys(errors).length;

  useEffect(() => {
    return () => {
      setContactFormState(() => ({
        isLoading: false,
        submitted: false,
        isEmailSent: false,
      }));
      reset();
    };
  }, []);

  const heading = 'Contact Us in One Click';
  const excerpt = `We value your feedback and questions. 
                   Complete the form below, and our team will respond to you promptly.`;

  const { ref, inView } = useInView({
    triggerOnce: true,
    threshold: 0,
  });

  return (
    <Layout>
      <Wrapper className={styles.wrapper}>
        <Breadcrumbs />
        <div ref={ref} className={styles.info}>
          <h1 className={`heading__1 ${styles.heading} ${inView && styles.animate__heading}`}>
            {createAnimationArr(heading, styles.word__wrapper, 0, 0.01)}
          </h1>
          <p className={`excerpt ${styles.excerpt} ${inView && styles.animate__excerpt}`}>
            {createAnimationArr(excerpt, styles.word__wrapper, 0.3, 0.01)}
          </p>
        </div>
        {contactFormState.submitted ? (
          <div className={styles.formSubmissionResult}>
            {contactFormState.isEmailSent ? <SuccessIcon /> : <ErrorIcon />}
            <h2 className={styles.formSubmissionResult__title}>
              {contactFormState.isEmailSent
                ? 'Success! Your message has been sent!'
                : 'Oops! Something went wrong!'}
            </h2>
            <p className={styles.formSubmissionResult__desc}>
              {contactFormState.isEmailSent
                ? 'Thank you! We will get to you as soon as possible.'
                : 'There was an error trying to send your message. Please try again later.'}
            </p>
            {contactFormState.isEmailSent ? (
              <Button
                buttonUrl={'/'}
                ariaLabel="Back to home page link"
                action="interactive"
                color="primary-dark">
                Ok, Thanks!
              </Button>
            ) : (
              <Button
                action={'standard'}
                aria-label={'Try again button'}
                color="primary-dark"
                onClick={() =>
                  setContactFormState((prev) => ({
                    ...prev,
                    submitted: false,
                  }))
                }>
                Try Again!
              </Button>
            )}
          </div>
        ) : (
          <form action="" className={styles.form} onSubmit={handleSubmit(onSubmit)} noValidate>
            <h2 className={styles.formTitle}>Ready To Get Started?</h2>
            <div className={styles.inputsContainer}>
              <div className={styles.inputWrapper}>
                <label htmlFor="contact-name" className={styles.lable}>
                  Name
                </label>
                <input
                  type="text"
                  id="contact-name"
                  placeholder="Name"
                  className={classNames(styles.input, {
                    [styles['input--error']]: errors.Name,
                  })}
                  aria-label="Name"
                  {...register('Name', { required: true })}
                />
                {errors.Name && <p className={styles.error}>{errors.Name.message}</p>}
              </div>
              <div className={styles.inputWrapper}>
                <label htmlFor="contact-email" className={styles.lable}>
                  E-mail
                </label>
                <input
                  type="email"
                  id="contact-email"
                  placeholder="E-mail"
                  className={classNames(styles.input, {
                    [styles['input--error']]: errors.Email,
                  })}
                  aria-label="E-mail"
                  {...register('Email', { required: true })}
                />
                {errors.Email && <p className={styles.error}>{errors.Email.message}</p>}
              </div>
              <div className={styles.area__wrapper}>
                <label htmlFor="contact-area" className={styles.lable}>
                  Message
                </label>
                <textarea
                  id="contact-area"
                  placeholder="Type Something"
                  className={classNames(styles.area, {
                    [styles['area--error']]: errors.Message,
                  })}
                  aria-label="Text area for your message"
                  {...register('Message', { required: true })}
                />
                {errors.Message && <p className={styles.error}>{errors.Message.message}</p>}
              </div>

              <FormAgreements register={register} errors={errors} showSubscribe />
            </div>
            <Button
              type="submit"
              aria-label="Submit Request"
              className={classNames(styles.button, {
                [styles[`button--isLoading`]]: contactFormState.isLoading,
                [styles[`button--locked`]]: isValidationErrors,
              })}
              action="interactive"
              color="primary-dark"
              isLoading={contactFormState.isLoading}>
              {contactFormState.isLoading ? <LoaderIcon /> : 'Submit Request'}
            </Button>
          </form>
        )}
      </Wrapper>
      <SubscribeSection colorScheme="dark" />
    </Layout>
  );
};

export default Contacts;
